import React, { useEffect, useState } from "react";
import { Breadcrumb, Layout, Table, Button, Input, Space, Tag, Modal, Form, Card, Typography, notification, Select } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { FormattedMessage, useIntl } from "react-intl";
import { SearchOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { IApiUser } from "../../services/nestjs-base-api/types";
import { useGetAllUsersQuery, useUpdateUserMutation, useDeleteUserMutation, useCreateOrganisationUserMutation } from "../../services/nestjs-base-api/admin-api";
import { UserRoleEnum } from "../../enums/UserRoleEnum";
import { GeneralStatusEnum } from "../../enums/GeneralStatusEnum";
import { Utils } from "../../utils/utils";
import type { ColumnsType } from 'antd/es/table';

const { Content } = Layout;
const { Title } = Typography;
const { Search } = Input;

const AdminUsersPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const [searchText, setSearchText] = useState("");
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IApiUser | null>(null);
  const [editForm] = Form.useForm();
  const [createForm] = Form.useForm();
  
  // API hooks
  const { data: usersData, error, isLoading, refetch } = useGetAllUsersQuery({ 
    jwtToken: user.jwtToken 
  });
  const [updateUser] = useUpdateUserMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [createUser] = useCreateOrganisationUserMutation();

  useEffect(() => {
    if (error) {
      if ('status' in error) {
        if (error.status === 401) {
          logout();
        }
      }
    }
  }, [error, logout]);

  // Handle user data after fetching
  const users = usersData || [];

  // Handle user edit
  const handleEditUser = (record: IApiUser) => {
    setSelectedUser(record);
    
    // Parse roles from JSON string
    const roles = JSON.parse(record.roles || '[]');
    
    editForm.setFieldsValue({
      email: record.email,
      roles: roles,
      status: record.status,
      organisationId: record.organisation?.id
    });
    
    setEditModalVisible(true);
  };

  // Handle user delete
  const handleDeleteUser = (id: number, email: string) => {
    Modal.confirm({
      title: intl.formatMessage(
        { id: 'admin.users.deleteConfirmTitle' },
        { email }
      ) || `Delete User: ${email}`,
      content: intl.formatMessage(
        { id: 'admin.users.deleteConfirmMessage' },
        { email }
      ) || `Are you sure you want to delete user ${email}? This action cannot be undone.`,
      okText: intl.formatMessage({ id: 'common.delete' }) || 'Delete',
      cancelText: intl.formatMessage({ id: 'common.cancel' }) || 'Cancel',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteUser({ 
            jwtToken: user.jwtToken,
            userId: id
          }).unwrap();
          
          notification.success({
            message: intl.formatMessage({ id: 'admin.users.deleteSuccess' }) || 'User Deleted',
            description: intl.formatMessage(
              { id: 'admin.users.deleteSuccessDesc' },
              { email }
            ) || `User ${email} has been successfully deleted.`,
          });
          
          refetch();
        } catch (err) {
          notification.error({
            message: intl.formatMessage({ id: 'admin.users.deleteError' }) || 'Delete Failed',
            description: intl.formatMessage({ id: 'admin.users.deleteErrorDesc' }) || 'Failed to delete user. Please try again.',
          });
        }
      },
    });
  };

  // Handle user update
  const handleUpdateUser = async (values: any) => {
    if (!selectedUser) return;
    
    try {
      // Convert roles array to JSON string
      const rolesString = JSON.stringify(values.roles);
      
      await updateUser({
        jwtToken: user.jwtToken,
        userId: selectedUser.id,
        payload: {
          email: values.email,
          roles: rolesString,
          status: values.status
        }
      }).unwrap();
      
      notification.success({
        message: intl.formatMessage({ id: 'admin.users.updateSuccess' }) || 'User Updated',
        description: intl.formatMessage(
          { id: 'admin.users.updateSuccessDesc' },
          { email: values.email }
        ) || `User ${values.email} has been successfully updated.`,
      });
      
      setEditModalVisible(false);
      refetch();
    } catch (err) {
      notification.error({
        message: intl.formatMessage({ id: 'admin.users.updateError' }) || 'Update Failed',
        description: intl.formatMessage({ id: 'admin.users.updateErrorDesc' }) || 'Failed to update user. Please try again.',
      });
    }
  };

  // Handle user create
  const handleCreateUser = async (values: any) => {
    try {
      // Convert roles array to JSON string
      const rolesString = JSON.stringify(values.roles);
      
      await createUser({
        jwtToken: user.jwtToken,
        organisationId: values.organisationId,
        payload: {
          email: values.email,
          roles: rolesString,
          status: values.status
        }
      }).unwrap();
      
      notification.success({
        message: intl.formatMessage({ id: 'admin.users.createSuccess' }) || 'User Created',
        description: intl.formatMessage(
          { id: 'admin.users.createSuccessDesc' },
          { email: values.email }
        ) || `User ${values.email} has been successfully created.`,
      });
      
      setCreateModalVisible(false);
      createForm.resetFields();
      refetch();
    } catch (err) {
      notification.error({
        message: intl.formatMessage({ id: 'admin.users.createError' }) || 'Create Failed',
        description: intl.formatMessage({ id: 'admin.users.createErrorDesc' }) || 'Failed to create user. Please try again.',
      });
    }
  };

  // Table columns
  const columns: ColumnsType<IApiUser> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a: IApiUser, b: IApiUser) => a.id - b.id,
      width: 80,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a: IApiUser, b: IApiUser) => a.email.localeCompare(b.email),
      filterSearch: true,
    },
    {
      title: 'Organisation',
      dataIndex: 'organisation',
      key: 'organisation',
      render: (organisation: any) => organisation?.name || '-',
      sorter: (a: IApiUser, b: IApiUser) => (a.organisation?.name || '').localeCompare(b.organisation?.name || ''),
    },
    {
      title: 'Roles',
      dataIndex: 'roles',
      key: 'roles',
      render: ((roles: string) => {
        try {
          const parsedRoles = JSON.parse(roles || '[]');
          
          return (
            <Space>
              {parsedRoles.map((role: UserRoleEnum) => (
                <Tag 
                  color={role === UserRoleEnum.SuperAdmin ? 'red' : (role === UserRoleEnum.OrganisationAdmin ? 'blue' : 'green')}
                  key={role}
                >
                  {Utils.readableUserRole(role)}
                </Tag>
              ))}
            </Space>
          );
        } catch (e) {
          return '-';
        }
      }),
      filters: [
        { text: 'Super Admin', value: UserRoleEnum.SuperAdmin },
        { text: 'Organisation Admin', value: UserRoleEnum.OrganisationAdmin },
        { text: 'Organisation Viewer', value: UserRoleEnum.OrganisationViewer },
      ],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: ((status: GeneralStatusEnum) => (
        <Tag color={status === GeneralStatusEnum.Active ? 'green' : 'red'}>
          {Utils.readableGeneralStatus(status)}
        </Tag>
      )),
      filters: [
        { text: 'Active', value: GeneralStatusEnum.Active },
        { text: 'Inactive', value: GeneralStatusEnum.Inactive },
      ],
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: IApiUser) => (
        <Space size="small">
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={() => handleEditUser(record)} 
            title="Edit"
          />
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => handleDeleteUser(record.id, record.email)} 
            title="Delete"
            disabled={record.email === user.email} // Prevent deleting the current user
          />
        </Space>
      ),
    },
  ];

  return (
    <Content style={{ margin: '0 16px' }}>
      <Breadcrumb style={{ margin: '16px 0' }}>
        <Breadcrumb.Item>RecogniX</Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.adminDashboardName'} defaultMessage="Admin Dashboard" />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.usersName'} defaultMessage="Users" />
        </Breadcrumb.Item>
      </Breadcrumb>
      
      <Card
        title={
          <Title level={2}>
            <FormattedMessage id={'admin.users.title'} defaultMessage="Manage Users" />
          </Title>
        }
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => setCreateModalVisible(true)}
          >
            <FormattedMessage id={'admin.users.create'} defaultMessage="Create User" />
          </Button>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Search
            placeholder={intl.formatMessage({ id: 'admin.users.searchPlaceholder' }) || "Search users by email"}
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            onSearch={value => setSearchText(value)}
            style={{ marginBottom: 16 }}
          />
          
          <Table 
            dataSource={users} 
            columns={columns} 
            rowKey="id"
            loading={isLoading}
            pagination={{ pageSize: 10 }}
          />
        </Space>
      </Card>
      
      {/* Edit User Modal */}
      <Modal
        title={intl.formatMessage({ id: 'admin.users.editModalTitle' }) || "Edit User"}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
      >
        <Form 
          form={editForm} 
          layout="vertical" 
          onFinish={handleUpdateUser}
        >
          <Form.Item
            name="email"
            label={intl.formatMessage({ id: 'admin.users.emailLabel' }) || "Email"}
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input disabled={selectedUser?.email === user.email} />
          </Form.Item>
          
          <Form.Item
            name="roles"
            label={intl.formatMessage({ id: 'admin.users.rolesLabel' }) || "Roles"}
            rules={[{ required: true, message: 'Please select at least one role' }]}
          >
            <Select 
              mode="multiple" 
              placeholder="Select roles"
              disabled={selectedUser?.email === user.email}
            >
              <Select.Option value={UserRoleEnum.SuperAdmin}>Super Admin</Select.Option>
              <Select.Option value={UserRoleEnum.OrganisationAdmin}>Organisation Admin</Select.Option>
              <Select.Option value={UserRoleEnum.OrganisationViewer}>Organisation Viewer</Select.Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="status"
            label={intl.formatMessage({ id: 'admin.users.statusLabel' }) || "Status"}
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select disabled={selectedUser?.email === user.email}>
              <Select.Option value={GeneralStatusEnum.Active}>Active</Select.Option>
              <Select.Option value={GeneralStatusEnum.Inactive}>Inactive</Select.Option>
            </Select>
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" disabled={selectedUser?.email === user.email}>
                <FormattedMessage id={'common.update'} defaultMessage="Update" />
              </Button>
              <Button onClick={() => setEditModalVisible(false)}>
                <FormattedMessage id={'common.cancel'} defaultMessage="Cancel" />
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      
      {/* Create User Modal */}
      <Modal
        title={intl.formatMessage({ id: 'admin.users.createModalTitle' }) || "Create New User"}
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        footer={null}
      >
        <Form 
          form={createForm} 
          layout="vertical" 
          onFinish={handleCreateUser}
        >
          <Form.Item
            name="email"
            label={intl.formatMessage({ id: 'admin.users.emailLabel' }) || "Email"}
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item
            name="organisationId"
            label={intl.formatMessage({ id: 'admin.users.organisationLabel' }) || "Organisation"}
            rules={[{ required: true, message: 'Please select an organisation' }]}
          >
            <Select placeholder="Select organisation">
              {/* This would be populated with data from organisation API */}
              <Select.Option value={1}>Organisation 1</Select.Option>
              <Select.Option value={2}>Organisation 2</Select.Option>
              <Select.Option value={3}>Organisation 3</Select.Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="roles"
            label={intl.formatMessage({ id: 'admin.users.rolesLabel' }) || "Roles"}
            rules={[{ required: true, message: 'Please select at least one role' }]}
            initialValue={[UserRoleEnum.OrganisationViewer]}
          >
            <Select mode="multiple" placeholder="Select roles">
              <Select.Option value={UserRoleEnum.SuperAdmin}>Super Admin</Select.Option>
              <Select.Option value={UserRoleEnum.OrganisationAdmin}>Organisation Admin</Select.Option>
              <Select.Option value={UserRoleEnum.OrganisationViewer}>Organisation Viewer</Select.Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="status"
            label={intl.formatMessage({ id: 'admin.users.statusLabel' }) || "Status"}
            rules={[{ required: true, message: 'Please select status' }]}
            initialValue={GeneralStatusEnum.Active}
          >
            <Select>
              <Select.Option value={GeneralStatusEnum.Active}>Active</Select.Option>
              <Select.Option value={GeneralStatusEnum.Inactive}>Inactive</Select.Option>
            </Select>
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                <FormattedMessage id={'common.create'} defaultMessage="Create" />
              </Button>
              <Button onClick={() => setCreateModalVisible(false)}>
                <FormattedMessage id={'common.cancel'} defaultMessage="Cancel" />
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Content>
  );
};

export default AdminUsersPage;